import { readingHtml } from "./cards.js";
import { formatUnitValue } from "./units.js";
import { createParticleLayer, createWindArrowLayer, savedWindDisplay, savedWindParticleColor, windParticleColorParts } from "./great-lakes-conditions.js";
import { windParticleFields } from "./wind-fields.js";
import { formatAppDateTime } from "./app-time.js";

export { windParticleFields } from "./wind-fields.js";

let activeMap = null, payload = null, request = null, revision = 0, particleLayer = null, arrowLayer = null;
let lastKey = "";
let forecastState = () => ({ hour: 0, history: false, animation: false });
const cache = new Map();

export function windEnabled() {
  return document.querySelector("[data-gl-layer]")?.value === "wind";
}

export function windSpeedLabel(value) {
  return formatUnitValue(value * 3.6, "windSpeed", "kph", { decimals: 1 });
}

export function windReading(value, action = "", historical = false) {
  if (!value?.available) return readingHtml({ label: "Wind", value: "Unavailable", action });
  const direction = value.directionDegrees;
  const cardinal = direction === null ? "Calm" : ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][Math.round(direction / 45) % 8];
  return readingHtml({ label: "Wind at 10 m", value: windSpeedLabel(value.speedMetersPerSecond),
    note: direction === null ? cardinal : `From ${cardinal} (${Math.round(direction)}°)`,
    rows: [["Gusts", windSpeedLabel(value.gustMetersPerSecond)], [historical ? "Sample time" : "Forecast time", formatAppDateTime(value.validTime), { visuallyHiddenLabel: true }]], action });
}

function draw() {
  particleLayer?.remove();
  particleLayer = null;
  arrowLayer?.remove();
  arrowLayer = null;
  if (!payload || !activeMap || !windEnabled()) return;
  const display = savedWindDisplay();
  const fields = windParticleFields(payload.points || []);
  if (display === "flow" && fields.length) particleLayer = createParticleLayer(activeMap, fields, {
    clipToGreatLakesWater: true,
    particleColor: windParticleColorParts(savedWindParticleColor())
  }).addTo(activeMap);
  else if (display === "arrows" && (payload.points || []).length) arrowLayer = createWindArrowLayer(activeMap, payload.points).addTo(activeMap);
}

export function showWindPayload(value, { historical = false } = {}) {
  payload = value;
  lastKey = historical ? `history:${value?.metadata?.history?.time || value?.metadata?.validTime || ""}` : "";
  draw();
  const status = document.querySelector("[data-gl-wind-status]");
  if (status) status.textContent = value
    ? `${historical ? "Saved " : ""}NOAA GFS · 10 m wind · ${formatAppDateTime(value.metadata.validTime)}`
    : historical ? "No saved wind sample at this time." : "";
}

export function setupWind(map, getState) {
  activeMap = map;
  forecastState = getState;
  document.addEventListener("great-lakes-animation-state", () => refreshWind());
  document.addEventListener("great-lakes-wind-color-changed", (event) => {
    particleLayer?.setParticleColor(windParticleColorParts(event.detail?.color));
  });
}

export async function refreshWind() {
  const state = forecastState();
  const status = document.querySelector("[data-gl-wind-status]");
  const setStatus = (text) => { if (status) status.textContent = text; };
  if (!windEnabled() || state.history || state.animation) {
    ++revision;
    request?.abort();
    request = null;
    particleLayer?.remove();
    particleLayer = null;
    arrowLayer?.remove();
    arrowLayer = null;
    payload = null;
    lastKey = "";
    setStatus(windEnabled() && state.history ? "Loading saved wind sample…" : "");
    return null;
  }
  const key = `${state.hour}:${Math.floor(Date.now() / 600000)}`;
  if (payload && lastKey === key) { draw(); return payload; }
  const version = ++revision;
  request?.abort();
  request = new AbortController();
  particleLayer?.remove();
  particleLayer = null;
  arrowLayer?.remove();
  arrowLayer = null;
  payload = null;
  setStatus("Loading NOAA wind forecast…");
  try {
    const result = cache.get(key) || await window.noaaGreatLakesApi.conditions({ layer: "wind", forecastHour: state.hour, signal: request.signal });
    if (version !== revision) return null;
    cache.set(key, result);
    while (cache.size > 6) cache.delete(cache.keys().next().value);
    payload = result;
    lastKey = key;
    draw();
    setStatus(`NOAA GFS · 10 m wind · ${formatAppDateTime(result.metadata.validTime)}`);
    return result;
  } catch (error) {
    if (version === revision && error.name !== "AbortError") setStatus("NOAA wind data is unavailable right now.");
    return null;
  }
}
